import React, { useEffect, useState } from 'react';
import { WithdrawRequest } from '../firebase/firestore';
import { adminApi } from '../api/admin';
import { getAuthToken } from '../firebase/auth-helpers';
import { useI18n } from '../i18n/I18nContext';
import './AdminWithdraws.css';

const AdminWithdraws: React.FC = () => {
  const { t } = useI18n();
  const [withdraws, setWithdraws] = useState<WithdrawRequest[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('REQUESTED');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    loadWithdraws();
  }, [selectedStatus]);

  const loadWithdraws = async () => {
    try {
      setLoading(true);
      // Lecture via l'API (AdminGuard) : les règles Firestore interdisent la lecture directe des retraits des autres comptes.
      const data: any[] = await adminApi.getWithdraws(selectedStatus || undefined);
      setWithdraws(
        data.map((w) => ({
          ...w,
          requestedAt: w.requestedAt ? new Date(w.requestedAt) : new Date(),
          processedAt: w.processedAt ? new Date(w.processedAt) : undefined,
        })),
      );
      setError(null);
    } catch (err: any) {
      setError(err.message || t.app.adminWithdraws.failedLoad);
    } finally {
      setLoading(false);
    }
  };

  const callAdminAPI = async (endpoint: string, body?: any) => {
    const token = await getAuthToken();
    if (!token) throw new Error(t.app.adminWithdraws.notAuthenticated);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || t.app.adminWithdraws.operationFailed);
    }

    return response.json();
  };

  const handleApprove = async (id: string) => {
    if (!window.confirm(t.app.adminWithdraws.approveConfirm)) return;

    try {
      setProcessing(id);
      await callAdminAPI(`admin/withdraws/${id}/approve`);
      await loadWithdraws();
    } catch (err: any) {
      alert(err.message || t.app.adminWithdraws.approveError);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = window.prompt(t.app.adminWithdraws.rejectPrompt);
    if (!reason) return;

    try {
      setProcessing(id);
      await callAdminAPI(`admin/withdraws/${id}/reject`, { reason });
      await loadWithdraws();
    } catch (err: any) {
      alert(err.message || t.app.adminWithdraws.rejectError);
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkPaid = async (id: string) => {
    const externalRef = window.prompt(t.app.adminWithdraws.markPaidPrompt);
    if (!externalRef?.trim()) {
      alert(t.app.adminWithdraws.markPaidRequired);
      return;
    }

    try {
      setProcessing(id);
      await callAdminAPI(`admin/withdraws/${id}/mark-paid`, { externalRef: externalRef.trim() });
      await loadWithdraws();
    } catch (err: any) {
      alert(err.message || t.app.adminWithdraws.updateError);
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkFailed = async (id: string) => {
    const error = window.prompt(t.app.adminWithdraws.markFailedPrompt);
    if (!error) return;

    try {
      setProcessing(id);
      await callAdminAPI(`admin/withdraws/${id}/mark-failed`, { error });
      await loadWithdraws();
    } catch (err: any) {
      alert(err.message || t.app.adminWithdraws.updateError);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="admin-loading">{t.app.adminWithdraws.loading}</div>;
  }

  if (error) {
    return <div className="admin-error">{t.app.common.error}: {error}</div>;
  }

  return (
    <div className="admin-withdraws">
      <h1>{t.app.adminWithdraws.title}</h1>

      <div className="status-filter">
        <label>{t.app.adminWithdraws.filterByStatus}</label>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="status-select"
        >
          <option value="">{t.app.adminWithdraws.all}</option>
          <option value="REQUESTED">REQUESTED</option>
          <option value="APPROVED">APPROVED</option>
          <option value="PROCESSING">PROCESSING</option>
          <option value="PAID">PAID</option>
          <option value="REJECTED">REJECTED</option>
          <option value="FAILED">FAILED</option>
        </select>
      </div>

      {withdraws.length === 0 ? (
        <div className="no-withdraws">{t.app.adminWithdraws.noRequests}</div>
      ) : (
        <div className="withdraws-list">
          {withdraws.map((withdraw: any) => (
            <div key={withdraw.id} className="withdraw-card admin-card">
              <div className="withdraw-header">
                <div className="withdraw-info">
                  <strong>#{withdraw.id.slice(0, 8)}</strong>
                  <span className={`status-badge status-${withdraw.status.toLowerCase()}`}>
                    {withdraw.status}
                  </span>
                </div>
                <div className="withdraw-date">{formatDate(withdraw.requestedAt)}</div>
              </div>

              <div className="withdraw-details">
                <div className="detail-row">
                  <span className="label">{t.app.adminWithdraws.agent}</span>
                  <span className="value">
                    {withdraw.agentUser?.agent?.displayName || withdraw.agentUser?.telegramUserId || t.app.common.notSet}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">{t.app.adminWithdraws.amount}</span>
                  <span className="value highlight">{withdraw.amountStars} {t.app.common.stars}</span>
                </div>
                <div className="detail-row">
                  <span className="label">{t.app.adminWithdraws.destination}</span>
                  <span className="value address">{withdraw.destination.address}</span>
                </div>
                {withdraw.processedAt && (
                  <div className="detail-row">
                    <span className="label">{t.app.adminWithdraws.processedOn}</span>
                    <span className="value">{formatDate(withdraw.processedAt)}</span>
                  </div>
                )}
                {withdraw.externalRef && (
                  <div className="detail-row">
                    <span className="label">{t.app.adminWithdraws.externalRef}</span>
                    <span className="value">{withdraw.externalRef}</span>
                  </div>
                )}
                {withdraw.error && (
                  <div className="detail-row error">
                    <span className="label">{t.app.adminWithdraws.errorLabel}</span>
                    <span className="value error-text">{withdraw.error}</span>
                  </div>
                )}
                {withdraw.rejectionReason && (
                  <div className="detail-row error">
                    <span className="label">{t.app.adminWithdraws.reason}</span>
                    <span className="value error-text">{withdraw.rejectionReason}</span>
                  </div>
                )}
              </div>

              <div className="admin-actions">
                {withdraw.status === 'REQUESTED' && (
                  <>
                    <button
                      onClick={() => handleApprove(withdraw.id)}
                      disabled={processing === withdraw.id}
                      className="action-btn approve-btn"
                    >
                      {processing === withdraw.id ? '...' : t.app.adminWithdraws.approve}
                    </button>
                    <button
                      onClick={() => handleReject(withdraw.id)}
                      disabled={processing === withdraw.id}
                      className="action-btn reject-btn"
                    >
                      {processing === withdraw.id ? '...' : t.app.adminWithdraws.reject}
                    </button>
                  </>
                )}
                {(withdraw.status === 'APPROVED' || withdraw.status === 'PROCESSING') && (
                  <>
                    <button
                      onClick={() => handleMarkPaid(withdraw.id)}
                      disabled={processing === withdraw.id}
                      className="action-btn paid-btn"
                    >
                      {processing === withdraw.id ? '...' : t.app.adminWithdraws.markPaid}
                    </button>
                    {withdraw.status === 'PROCESSING' && (
                      <button
                        onClick={() => handleMarkFailed(withdraw.id)}
                        disabled={processing === withdraw.id}
                        className="action-btn failed-btn"
                      >
                        {processing === withdraw.id ? '...' : t.app.adminWithdraws.markFailed}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminWithdraws;
